import React, { useId } from "react";
import PropTypes from "prop-types";
import ReactDOM from "react-dom";
import Button from "../../atoms/Button";
import PopupOverlay from "../Background/PopupOverlay";
import "./BandCreatorModal.scss";

export default function BandCreatorModal({
    portalRoot,
    isOpen,
    onClose,
    onSubmit,
    nameValue,
    descriptionValue = "",
    onNameChange,
    onDescriptionChange = () => {},
    showDescription = true,
    error = null,
    isSubmitting = false,
    title,
    subtitle,
    nameLabel,
    namePlaceholder,
    descriptionLabel = "",
    descriptionPlaceholder = "",
    cancelLabel,
    createLabel,
    creatingLabel,
}) {
    const titleId = useId();
    const subtitleId = `${titleId}-subtitle`;
    const nameInputId = useId();
    const descriptionInputId = useId();

    if (!isOpen || !portalRoot) {
        return null;
    }

    return ReactDOM.createPortal(
        <PopupOverlay onClose={onClose} closeOnOverlayClick closeOnEsc zIndex={7100}>
            <section
                className="create-band-modal"
                role="dialog"
                aria-modal="true"
                aria-labelledby={titleId}
                aria-describedby={subtitleId}
            >
                <header className="create-band-modal__header">
                    <p id={subtitleId} className="create-band-modal__eyebrow">{subtitle}</p>
                    <h2 id={titleId} className="create-band-modal__title">{title}</h2>
                </header>
                <form className="create-band-modal__form" onSubmit={onSubmit}>
                    <label className="create-band-modal__field" htmlFor={nameInputId}>
                        <span className="create-band-modal__field-label">{nameLabel}</span>
                        <input
                            id={nameInputId}
                            type="text"
                            value={nameValue}
                            onChange={(event) => onNameChange(event.target.value)}
                            placeholder={namePlaceholder}
                            minLength={3}
                            maxLength={128}
                            required
                        />
                    </label>
                    {showDescription && (
                        <label className="create-band-modal__field" htmlFor={descriptionInputId}>
                            <span className="create-band-modal__field-label">{descriptionLabel}</span>
                            <textarea
                                id={descriptionInputId}
                                value={descriptionValue}
                                onChange={(event) => onDescriptionChange(event.target.value)}
                                placeholder={descriptionPlaceholder}
                                maxLength={512}
                                rows={3}
                            />
                        </label>
                    )}
                    {error && (
                        <p className="create-band-modal__error" role="alert">
                            {error}
                        </p>
                    )}
                    <div className="create-band-modal__actions">
                        <Button
                            className="create-band-modal__cancel"
                            variant="secondary"
                            shape="pill"
                            text={cancelLabel}
                            onClick={onClose}
                            disabled={isSubmitting}
                            type="button"
                        />
                        <Button
                            className="create-band-modal__submit"
                            variant="gradient"
                            shape="pill"
                            text={isSubmitting ? creatingLabel : createLabel}
                            type="submit"
                            disabled={isSubmitting}
                        />
                    </div>
                </form>
            </section>
        </PopupOverlay>,
        portalRoot
    );
}

BandCreatorModal.propTypes = {
    portalRoot: PropTypes.any,
    isOpen: PropTypes.bool.isRequired,
    onClose: PropTypes.func.isRequired,
    onSubmit: PropTypes.func.isRequired,
    nameValue: PropTypes.string.isRequired,
    descriptionValue: PropTypes.string,
    onNameChange: PropTypes.func.isRequired,
    onDescriptionChange: PropTypes.func,
    showDescription: PropTypes.bool,
    error: PropTypes.string,
    isSubmitting: PropTypes.bool,
    title: PropTypes.string.isRequired,
    subtitle: PropTypes.string.isRequired,
    nameLabel: PropTypes.string.isRequired,
    namePlaceholder: PropTypes.string.isRequired,
    descriptionLabel: PropTypes.string,
    descriptionPlaceholder: PropTypes.string,
    cancelLabel: PropTypes.string.isRequired,
    createLabel: PropTypes.string.isRequired,
    creatingLabel: PropTypes.string.isRequired,
};
